import apiClient, { resolveApiUrl } from './apiClient';

export interface RedDotDraft {
  id: string;
  status: string;
  goalId?: string;
}

export interface RedDotSubmitResult {
  id: string;
  status: string;
  protocolsGenerated: number;
  tasksGenerated: number;
  actionStatements: string[];
  /** Non-blocking advisories (e.g. owner-less Red Dot: no active BCBA with manage). */
  warnings?: string[];
}

export interface RedDotTask {
  id: string;
  name: string;
  type: string;
  status: string;
  description?: string;
  displayOrder?: number;
  groupCode?: string;
  groupName?: string;
  triggerSource?: string;
  code?: string;
  parentTaskId?: string;
  assignedTo?: { id: string; firstName: string; lastName: string };
  /** True when the current viewer is a watcher on this task (server-annotated). */
  watchedByMe?: boolean;
  /**
   * Server-authoritative flag: true for the assignee, task:admin holders, and the
   * Red Dot workflow owner; false for watchers and other viewers. Treat
   * missing/undefined as false (fail-safe read-only).
   */
  canAct?: boolean;
  formDefinitionCode?: string;
  childTasks?: RedDotTask[];
  referencedTaskId?: string;
  referencedTask?: { id: string; name: string; status: string };
  referencingTasks?: { id: string; name: string; status: string }[];
  createdAt?: string;
  dueDate?: string;
  statusReason?: string;
  origin?: string;
  source?: string;
  workflowInstanceId?: string;
}

// Protocol groups derived from parent tasks
export interface RedDotTaskGroup {
  parentTask: RedDotTask;
  groupCode: string;
  groupName: string;
  triggerSource: string;
  tasks: RedDotTask[];
}

export interface CommentAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  storageUrl: string;
  uploadedAt: string;
}

export interface TaskDocument {
  id: string;
  taskId: string;
  kind: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  /**
   * Server-computed: can this document's bytes actually be served? False for a
   * legacy disk-backed row whose file is gone (the common case in any deployed
   * env — the container filesystem is ephemeral), so the UI can tombstone it
   * before the user clicks rather than making them discover it via a 404. A
   * legacy file that DOES still exist reports true and stays fully openable.
   */
  isAvailable: boolean;
  uploadedBy?: { firstName: string; lastName: string } | null;
}

export interface CommentLink {
  url: string;
  title?: string | null;
  addedAt: string;
}

export interface RedDotComment {
  id: string;
  body: string;
  resolved: boolean;
  createdAt: string;
  workflowInstanceId?: string;
  taskId?: string;
  attachments?: CommentAttachment[];
  links?: CommentLink[];
  author: { id: string; firstName: string; lastName: string; email?: string };
  replies?: RedDotComment[];
  /** ADO #656: set when the author last edited the comment. */
  editedAt?: string | null;
  /** ADO #656: soft-deleted — body, attachments and links come back empty. */
  isDeleted?: boolean;
}

export interface MentionableUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  centerName: string | null;
  roleNames: string[];
}

/** A workflow/task event surfaced in the Activity feed (ADO 473: `actor` carries the
 *  clinician who performed the action, e.g. the user who completed a PA/Red Dot). */
export interface WorkflowFeedEvent {
  id: string;
  eventType: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string | null;
  createdAt: string;
  actor?: { id: string; firstName: string; lastName: string };
}

export type ActivityFeedItem =
  | { type: 'comment'; createdAt: string; data: RedDotComment }
  | { type: 'event'; createdAt: string; data: WorkflowFeedEvent };

export interface RedDotDetail {
  id: string;
  status: string;
  workflowType: string;
  submittedAt: string | null;
  completedAt: string | null;
  closedAt: string | null;
  slaDeadline: string | null;
  /**
   * SLA window (calendar days) this Red Dot was opened under. Null on rows submitted
   * before #511 recorded it; consumers fall back to the platform default of 14.
   */
  slaTotalDays?: number | null;
  submissionData: any;
  draftData: any;
  /** Reason recorded for the current ON_HOLD state; null once resolved/resumed. */
  statusReason: string | null;
  client: { id: string; firstName: string; lastName: string } | null;
  submittedBy: { id: string; firstName: string; lastName: string; email: string } | null;
  assignedTo: { id: string; firstName: string; lastName: string; email: string } | null;
  tasks: RedDotTask[];
  workflowEvents: any[];
  workflowAnswers: any[];
  comments?: RedDotComment[];
}

/** An open (non-terminal leaf) task blocking a gated Complete. */
export interface RedDotOpenTask {
  id: string;
  name: string;
  status: string;
}

/** `PATCH /red-dot/:id/status` succeeded and the transition applied. */
export interface RedDotStatusUpdateResult {
  id: string;
  status: string;
  gated?: false;
}

/** Complete was attempted with open tasks and no justification — the caller must
 *  re-submit with a `reason` to force the transition. */
export interface RedDotStatusGatedResult {
  id: string;
  status: string;
  gated: true;
  openTasks: RedDotOpenTask[];
  message: string;
}

export type RedDotStatusUpdateResponse = RedDotStatusUpdateResult | RedDotStatusGatedResult;

/** A Red Dot / CTA submission with no owning BCBA — surfaced in the Org Admin
 *  "Needs Assignment" queue (ADO 535). */
export interface NeedsAssignmentItem {
  id: string;
  client: { id: string; firstName: string; lastName: string } | null;
  submittedBy: { id: string; firstName: string; lastName: string } | null;
  submittedAt: string | null;
  type: 'RED_DOT' | 'CTA';
  status: string;
}

/** Resolved owner for the Review step — `unassigned: true` means no eligible BCBA
 *  was found and the submission will land in the Needs Assignment queue (ADO 535). */
export interface AssignmentPreview {
  bcbaName: string | null;
  unassigned: boolean;
}

export interface RedDotListItem {
  id: string;
  status: string;
  workflowType: string;
  createdAt: string;
  submittedAt: string | null;
  /** Resolution timestamps — analytics resolution math uses completedAt ?? closedAt. */
  completedAt?: string | null;
  closedAt?: string | null;
  slaDeadline: string | null;
  /**
   * SLA window (calendar days) this Red Dot was opened under. Null on rows submitted
   * before #511 recorded it; consumers fall back to the platform default of 14.
   */
  slaTotalDays?: number | null;
  submissionData: any;
  tasks?: RedDotTask[];
  client: {
    id: string;
    firstName: string;
    lastName: string;
    /** Runtime payload includes primaryCenter from backend select (aliased clientMember→client). */
    primaryCenter?: { id?: string; name?: string; city?: string | null; state?: string | null } | null;
  } | null;
  submittedBy: { id: string; firstName: string; lastName: string } | null;
  assignedTo: { id: string; firstName: string; lastName: string } | null;
}

// Canonical protocol display order, matching the original Red Dot workflow sequence.
// Groups not listed here keep their API order after the known protocols; the
// "Additional Tasks" bucket (_ungrouped) always sorts last.
const PROTOCOL_DISPLAY_ORDER: string[] = [
  'fidelity', // Fidelity Protocol
  'data_deep_dive', // Data Deep Dive
  'motivation', // Motivation Protocol
  'low_trial_count', // Low Trial Count
  'goal_implementation', // Goal Implementation Protocol
  'challenging_behavior', // Challenging Behavior Protocol
  'high_performance', // High Performance Review
];

function protocolOrderRank(groupCode: string): number {
  if (groupCode === '_ungrouped') return Number.MAX_SAFE_INTEGER;
  const idx = PROTOCOL_DISPLAY_ORDER.indexOf(groupCode);
  // Unknown protocols sort after the known sequence but before Additional Tasks.
  return idx === -1 ? PROTOCOL_DISPLAY_ORDER.length : idx;
}

// Helper: group tasks by parent-child hierarchy for display
// API now returns parent tasks (parentTaskId=null) with childTasks nested
export function groupTasksByProtocol(tasks: RedDotTask[]): RedDotTaskGroup[] {
  const groups: RedDotTaskGroup[] = [];
  const ungroupedTasks: RedDotTask[] = [];

  for (const task of tasks) {
    if (task.childTasks && task.childTasks.length > 0) {
      // This is a parent task — use it as the group header
      groups.push({
        parentTask: task,
        groupCode: task.code || task.groupCode || task.id,
        groupName: task.name || task.groupName || 'Protocol',
        triggerSource: task.triggerSource || '',
        tasks: task.childTasks,
      });
    } else if (!task.parentTaskId && (!task.childTasks || task.childTasks.length === 0)) {
      // Standalone task (no parent, no children)
      ungroupedTasks.push(task);
    }
    // Tasks with parentTaskId but no childTasks are already nested in their parent's childTasks
  }

  if (ungroupedTasks.length > 0) {
    groups.push({
      parentTask: { id: '_ungrouped', name: 'Additional Tasks', type: '', status: '' },
      groupCode: '_ungrouped',
      groupName: 'Additional Tasks',
      triggerSource: '',
      tasks: ungroupedTasks,
    });
  }

  // Order protocol groups to match the original Red Dot workflow sequence.
  // Stable for equal ranks (preserves API order among unknown protocols).
  return groups
    .map((group, index) => ({ group, index }))
    .sort(
      (a, b) =>
        protocolOrderRank(a.group.groupCode) - protocolOrderRank(b.group.groupCode) ||
        a.index - b.index,
    )
    .map(({ group }) => group);
}

/** Fetch a signed URL (possibly relative) and return its contents as a Blob. */
async function fetchBlobFromUrl(rawUrl: string): Promise<Blob> {
  const url = resolveApiUrl(rawUrl);
  if (!url) throw new Error('No PDF URL returned');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch PDF (${res.status})`);
  return res.blob();
}

/**
 * Error code returned by the API (404 body) when a task document's row exists and
 * is readable but its bytes are NOT in storage — the user must re-upload; a retry
 * can never succeed. Callers branch on this to pick the right call to action.
 */
export const FILE_UNAVAILABLE_CODE = 'FILE_UNAVAILABLE';

/** Every comment attachment's `storageUrl` starts with this (see the API's UploadService). */
const COMMENT_UPLOAD_PREFIX = '/api/v1/uploads/comments/';

/** True when an error carries the API's FILE_UNAVAILABLE code. */
export function isFileUnavailableError(e: unknown): boolean {
  return (e as { code?: string } | null)?.code === FILE_UNAVAILABLE_CODE;
}

/**
 * `responseType: 'blob'` applies to error responses too, so a JSON error body
 * arrives as a Blob and `err.response.data.code` is always undefined. Read it
 * back into an Error carrying the server's `code` (e.g. FILE_UNAVAILABLE) so
 * callers can distinguish "gone forever" from "try again".
 */
async function decodeBlobError(e: unknown): Promise<unknown> {
  const err = e as { response?: { data?: unknown; status?: number } };
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return e;
  try {
    const parsed = JSON.parse(await data.text()) as { code?: string; message?: string };
    const decoded = new Error(parsed.message || 'Request failed') as Error & {
      code?: string;
      status?: number;
    };
    decoded.code = parsed.code;
    decoded.status = err.response?.status;
    return decoded;
  } catch {
    return e;
  }
}

const redDotService = {
  startDraft: async (data: { clientId: string; goalText: string; goalId?: string; option: '1' | '2' }): Promise<RedDotDraft> => {
    const res = await apiClient.post('/api/v1/red-dot/start', data);
    return res.data;
  },

  /** ADO #619: record a Continue on the welcome message (copy version the user saw). */
  acknowledge: async (copyVersion: number): Promise<void> => {
    await apiClient.post('/api/v1/red-dot/acknowledgement', { copyVersion });
  },

  saveDraft: async (id: string, draftData: any): Promise<RedDotDraft> => {
    const res = await apiClient.patch(`/api/v1/red-dot/${id}/draft`, { draftData });
    return res.data;
  },

  submit: async (id: string): Promise<RedDotSubmitResult> => {
    const res = await apiClient.post(`/api/v1/red-dot/${id}/submit`);
    return res.data;
  },

  getDetail: async (id: string): Promise<RedDotDetail> => {
    const res = await apiClient.get(`/api/v1/red-dot/${id}`);
    return res.data;
  },

  list: async (params?: { status?: string; clientId?: string; scope?: 'mine' }): Promise<RedDotListItem[]> => {
    const res = await apiClient.get('/api/v1/red-dot', { params });
    return res.data;
  },

  updateStatus: async (id: string, status: string, reason?: string): Promise<RedDotStatusUpdateResponse> => {
    const res = await apiClient.patch(`/api/v1/red-dot/${id}/status`, { status, reason });
    return res.data;
  },

  softDelete: async (id: string, reason: string): Promise<any> => {
    const res = await apiClient.delete(`/api/v1/red-dot/${id}`, { data: { reason } });
    return res.data;
  },

  getTaskFormSchema: async (taskId: string): Promise<any> => {
    const res = await apiClient.get(`/api/v1/red-dot/tasks/${taskId}/form-schema`);
    return res.data;
  },

  createFollowUp: async (taskId: string, dueDate?: string): Promise<any> => {
    const res = await apiClient.post(`/api/v1/red-dot/tasks/${taskId}/follow-up`, { dueDate });
    return res.data;
  },

  setFidelityMethod: async (taskId: string, method: 'dtt_fidelity' | 'net_fidelity'): Promise<any> => {
    const res = await apiClient.patch(`/api/v1/red-dot/tasks/${taskId}/method`, { method });
    return res.data;
  },

  submitTaskForm: async (taskId: string, formData: any): Promise<any> => {
    const res = await apiClient.post(`/api/v1/red-dot/tasks/${taskId}/form`, { formData });
    return res.data;
  },

  getTaskFormSubmission: async (taskId: string): Promise<any> => {
    const res = await apiClient.get(`/api/v1/red-dot/tasks/${taskId}/form-submission`);
    return res.data;
  },

  updateTaskForm: async (taskId: string, formData: any): Promise<any> => {
    const res = await apiClient.put(`/api/v1/red-dot/tasks/${taskId}/form`, { formData });
    return res.data;
  },

  saveTaskFormDraft: async (taskId: string, formData: any): Promise<any> => {
    const res = await apiClient.put(`/api/v1/red-dot/tasks/${taskId}/form-draft`, { formData });
    return res.data;
  },

  addNote: async (id: string, text: string): Promise<any> => {
    const res = await apiClient.post(`/api/v1/red-dot/${id}/notes`, { text });
    return res.data;
  },

  getAssignableUsers: async (
    workflowInstanceId: string,
  ): Promise<
    {
      id: string;
      firstName: string;
      lastName: string;
      centerName: string | null;
      roleName: string | null;
      roleNames: string[];
      /** True for the client's current Overseeing BCBA (caseload owner) — ADO 452. */
      isCaseloadOwner: boolean;
      /** False when `transferOwnership` would 400 this user (lacks `red_dot:manage`) — ADO 452. */
      canOwn: boolean;
    }[]
  > => {
    const res = await apiClient.get(`/api/v1/red-dot/${workflowInstanceId}/assignable-users`);
    return res.data;
  },

  /** Reassign a Red Dot's owner. `reason` is required server-side and surfaces in the Activity feed (ADO 452). */
  transferRedDotOwner: async (
    id: string,
    newOwnerId: string,
    reason: string,
  ): Promise<{ id: string; ownerId: string }> => {
    const res = await apiClient.patch(`/api/v1/red-dot/${id}/owner`, { newOwnerId, reason });
    return res.data;
  },

  getClientTechnicians: async (workflowInstanceId: string): Promise<{ id: string; firstName: string; lastName: string }[]> => {
    const res = await apiClient.get(`/api/v1/red-dot/${workflowInstanceId}/client-technicians`);
    return res.data;
  },

  /** Red Dot submission PDF — generated on demand, scoped + audited (view or download). */
  getSubmissionPdf: async (id: string, download = false): Promise<Blob> => {
    const res = await apiClient.post<{ data: { url: string; fileName: string } }>(
      `/api/v1/red-dot/${id}/submission-pdf${download ? '?download=1' : ''}`,
      {},
    );
    return fetchBlobFromUrl(res.data.data.url);
  },

  /** Task-form submission PDF — generated on demand, scoped + audited (view or download). */
  getTaskSubmissionPdf: async (taskId: string, download = false): Promise<Blob> => {
    const res = await apiClient.get<{ data: { url: string; fileName: string } }>(
      `/api/v1/red-dot/tasks/${taskId}/submission-pdf${download ? '?download=1' : ''}`,
    );
    return fetchBlobFromUrl(res.data.data.url);
  },

  /** Fillable log template (Sleep Log / Meal Log) for an action-item task — scoped + audited. */
  getTaskLogTemplate: async (taskId: string): Promise<Blob> => {
    try {
      const res = await apiClient.get(`/api/v1/red-dot/tasks/${taskId}/log-template`, {
        responseType: 'blob',
      });
      return res.data as Blob;
    } catch (e: unknown) {
      throw await decodeBlobError(e);
    }
  },

  // ── Task documents (uploaded files kept on a task) ──────────────────────────

  listTaskDocuments: async (taskId: string): Promise<TaskDocument[]> => {
    const res = await apiClient.get(`/api/v1/red-dot/tasks/${taskId}/documents`);
    return res.data as TaskDocument[];
  },

  uploadTaskDocument: async (taskId: string, file: File): Promise<TaskDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post(`/api/v1/red-dot/tasks/${taskId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data as TaskDocument;
  },

  downloadTaskDocument: async (taskId: string, docId: string, download = true): Promise<Blob> => {
    try {
      const res = await apiClient.get(
        `/api/v1/red-dot/tasks/${taskId}/documents/${docId}/download${download ? '?download=1' : ''}`,
        { responseType: 'blob' },
      );
      return res.data as Blob;
    } catch (e: unknown) {
      throw await decodeBlobError(e);
    }
  },

  /**
   * Fetch a comment attachment's bytes (ADO #232). `/api/v1/uploads/comments/*` needs the
   * bearer token, which an `<img src>`, `<iframe src>` or `<a href>` can't send — they 401.
   * Only comment-upload URLs are fetched, so the token never goes to any other address.
   */
  getCommentAttachment: async (storageUrl: string): Promise<Blob> => {
    if (!storageUrl.startsWith(COMMENT_UPLOAD_PREFIX)) {
      throw new Error('Unsupported attachment URL');
    }
    try {
      const res = await apiClient.get(storageUrl, { responseType: 'blob' });
      return res.data as Blob;
    } catch (e: unknown) {
      throw await decodeBlobError(e);
    }
  },

  /** Save a comment attachment to disk through an authenticated fetch (ADO #232). */
  downloadCommentAttachment: async (storageUrl: string, filename: string): Promise<void> => {
    const url = URL.createObjectURL(await redDotService.getCommentAttachment(storageUrl));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  deleteTaskDocument: async (taskId: string, docId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/red-dot/tasks/${taskId}/documents/${docId}`);
  },

  // Comment API
  addComment: async (
    id: string,
    body: string,
    opts?: { parentCommentId?: string; mentions?: string[]; links?: { url: string; title?: string }[]; files?: File[] },
  ): Promise<RedDotComment> => {
    if (opts?.files && opts.files.length > 0) {
      const formData = new FormData();
      formData.append('body', body);
      if (opts.parentCommentId) formData.append('parentCommentId', opts.parentCommentId);
      if (opts.mentions) formData.append('mentions', JSON.stringify(opts.mentions));
      if (opts.links) formData.append('links', JSON.stringify(opts.links));
      for (const file of opts.files) {
        formData.append('files', file);
      }
      const res = await apiClient.post(`/api/v1/red-dot/${id}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    }
    const res = await apiClient.post(`/api/v1/red-dot/${id}/comments`, {
      body,
      parentCommentId: opts?.parentCommentId,
      mentions: opts?.mentions,
      links: opts?.links,
    });
    return res.data;
  },

  listComments: async (id: string, taskId?: string): Promise<RedDotComment[]> => {
    const params = taskId ? { taskId } : undefined;
    const res = await apiClient.get(`/api/v1/red-dot/${id}/comments`, { params });
    return res.data;
  },

  listDiscussionComments: async (id: string): Promise<RedDotComment[]> => {
    const res = await apiClient.get(`/api/v1/red-dot/${id}/discussion`);
    return res.data;
  },

  getActivityFeed: async (id: string): Promise<ActivityFeedItem[]> => {
    const res = await apiClient.get(`/api/v1/red-dot/${id}/activity`);
    return res.data;
  },

  getTaskActivityFeed: async (taskId: string): Promise<ActivityFeedItem[]> => {
    const res = await apiClient.get(`/api/v1/red-dot/tasks/${taskId}/activity`);
    return res.data;
  },

  addTaskComment: async (
    taskId: string,
    body: string,
    opts?: { parentCommentId?: string; mentions?: string[]; files?: File[]; links?: { url: string; title?: string }[] },
  ): Promise<RedDotComment> => {
    if (opts?.files && opts.files.length > 0) {
      const formData = new FormData();
      formData.append('body', body);
      if (opts.parentCommentId) formData.append('parentCommentId', opts.parentCommentId);
      if (opts.mentions) formData.append('mentions', JSON.stringify(opts.mentions));
      if (opts.links) formData.append('links', JSON.stringify(opts.links));
      for (const file of opts.files) {
        formData.append('files', file);
      }
      const res = await apiClient.post(`/api/v1/red-dot/tasks/${taskId}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    }
    const res = await apiClient.post(`/api/v1/red-dot/tasks/${taskId}/comments`, {
      body,
      parentCommentId: opts?.parentCommentId,
      mentions: opts?.mentions,
      links: opts?.links,
    });
    return res.data;
  },

  // ADO #656 — author-only; the server returns 403 to anyone else.
  editComment: async (
    commentId: string,
    data: { body: string; mentions?: string[] },
  ): Promise<RedDotComment> => {
    const res = await apiClient.patch(`/api/v1/red-dot/comments/${commentId}`, data);
    return res.data;
  },

  deleteComment: async (commentId: string): Promise<{ id: string; isDeleted: true }> => {
    const res = await apiClient.delete(`/api/v1/red-dot/comments/${commentId}`);
    return res.data;
  },

  removeCommentAttachment: async (commentId: string, attachmentId: string): Promise<RedDotComment> => {
    const res = await apiClient.delete(
      `/api/v1/red-dot/comments/${commentId}/attachments/${encodeURIComponent(attachmentId)}`,
    );
    return res.data;
  },

  resolveComment: async (commentId: string): Promise<any> => {
    const res = await apiClient.patch(`/api/v1/red-dot/comments/${commentId}/resolve`);
    return res.data;
  },

  getUnreadCounts: async (id: string): Promise<{ byTask: Record<string, number>; workflowLevel: number; total: number }> => {
    const res = await apiClient.get(`/api/v1/red-dot/${id}/unread-counts`);
    return res.data;
  },

  getUnreadCountsBatch: async (ids: string[]): Promise<Record<string, Record<string, number>>> => {
    const res = await apiClient.post('/api/v1/comments/unread-batch', { workflowInstanceIds: ids });
    return res.data;
  },

  markAsRead: async (id: string, scopeType?: 'workflow' | 'task', scopeId?: string): Promise<void> => {
    await apiClient.post(`/api/v1/red-dot/${id}/mark-read`, {
      ...(scopeType && { scopeType }),
      ...(scopeId && { scopeId }),
    });
  },

  getMentionableUsers: async (id: string, query: string): Promise<MentionableUser[]> => {
    const res = await apiClient.get(`/api/v1/red-dot/${id}/mentionable-users`, { params: { q: query } });
    return res.data;
  },

  /** Role-scoped analytics dataset — all org-wide for users with clients:manage:all,
   *  own caseload otherwise. Requires red_dot:manage permission. */
  analytics: async (): Promise<RedDotListItem[]> => {
    const res = await apiClient.get('/api/v1/red-dot/analytics');
    return res.data;
  },

  /** Owner-less Red Dot/CTA submissions awaiting assignment — Org Admin queue
   *  (ADO 535). Requires action_center:full permission. */
  getNeedsAssignment: async (): Promise<NeedsAssignmentItem[]> => {
    const res = await apiClient.get<{ success: boolean; data: NeedsAssignmentItem[] }>(
      '/api/v1/red-dot/needs-assignment',
    );
    return res.data.data;
  },

  /** Badge count for the Needs Assignment queue. Requires action_center:full permission. */
  getNeedsAssignmentCount: async (): Promise<number> => {
    const res = await apiClient.get<{ success: boolean; data: { count: number } }>(
      '/api/v1/red-dot/needs-assignment/count',
    );
    return res.data.data.count;
  },

  /** Resolved owner preview for the wizard's Review step (ADO 535: `unassigned`
   *  replaces the removed `fallbackAdminName`). */
  getAssignmentPreview: async (clientId: string): Promise<AssignmentPreview> => {
    const res = await apiClient.get<AssignmentPreview>('/api/v1/red-dot/assignment-preview', {
      params: { clientId },
    });
    return res.data;
  },
};

export default redDotService;
